import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { ArrowUp, ArrowDown, ArrowUpDown, ChevronDown } from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { SortKey, SortOrder } from "../../utils/documentSorting";
import { documentGridTemplate } from "../../utils/fitDocumentColumns";
import {
  DATE_BASIS_SORT_KEY,
  type DocumentListDateBasis,
  setDocumentListDateBasis,
  useDocumentListDateBasis,
  useIsDocumentListDateBasisFixed,
} from "../../hooks/useDocumentListDateBasis";

interface DocumentListHeaderProps {
  /** Callback when select all checkbox is toggled */
  onSelectAll: (isSelected: boolean) => void;
  /** All documents are selected */
  isAllSelected: boolean;
  /** Some documents are selected (indeterminate state) */
  isIndeterminate: boolean;
  /** Show/hide Checkbox column */
  showCheckbox?: boolean;
  /** Show/hide Version column */
  showVersion?: boolean;
  /** Show/hide the date column (Uploaded / File modified, switchable) */
  showDateCreated?: boolean;
  /** Show/hide Status column */
  showStatus?: boolean;
  /** Show/hide the Uploader column */
  showUser?: boolean;
  /** Show/hide Tags column */
  showTags?: boolean;
  /** Show/hide Parent column */
  showParent?: boolean;
  /** Show/hide Actions column */
  showActions?: boolean;
  /** Current sort key */
  sortKey?: SortKey;
  /** Current sort order */
  sortOrder?: SortOrder;
  /** Callback when a sortable column header is clicked */
  onSort?: (key: SortKey) => void;
  /** Additional CSS classes */
  className?: string;
  /** Optional content rendered at the right edge of the header (e.g. pagination) */
  endContent?: React.ReactNode;
}

/**
 * DocumentListHeader - Organism component
 *
 * Table header row with column labels and select-all checkbox.
 * Defines the structure and layout for the document list table.
 */
export function DocumentListHeader({
  onSelectAll,
  isAllSelected,
  isIndeterminate,
  showCheckbox = true,
  showVersion = true,
  showDateCreated = true,
  showStatus = true,
  showUser = true,
  showTags = true,
  showParent = false,
  showActions = true,
  sortKey,
  sortOrder,
  onSort,
  className,
  endContent,
}: DocumentListHeaderProps) {
  const { t } = useLanguage();
  // Shared with every row kind, so the labels sit over their cells.
  const gridColumns = documentGridTemplate({
    showCheckbox,
    showDateCreated,
    showUser,
    showVersion,
    showStatus,
    showTags,
    showParent,
    showActions,
  });

  // Shared column-label style — matches the Issues page header (the table-design
  // source of truth): uppercase, tracking-wide, muted, `text-[10px]`.
  // Applied per-label rather than on the parent grid so the inline Search
  // input doesn't inherit the uppercase transform.
  const colLabel = "text-[10px] font-semibold uppercase tracking-wide text-muted-foreground";

  return (
    // Plain (no filled background) header with a single thin bottom border,
    // matching the Issues page table.
    <div className={cn("relative border-b px-6 py-1.5", className)}>
      <div
        className="grid items-center gap-4"
        style={{
          gridTemplateColumns: gridColumns,
        }}
      >
        {/* Select All Checkbox */}
        {showCheckbox && (
          <div className="flex items-center justify-center">
            <Checkbox
              checked={isAllSelected}
              indeterminate={isIndeterminate}
              onCheckedChange={(checked) => onSelectAll(checked === true)}
              aria-label={t.documents.table.selectAll}
            />
          </div>
        )}

        {/* Column Labels */}
        <div className="flex items-center gap-2">
          <span
            className={cn(colLabel, onSort && "cursor-pointer select-none")}
            onClick={onSort ? () => onSort("name") : undefined}
          >
            {t.documents.table.name}
            {onSort && <SortIcon field="name" activeField={sortKey} direction={sortOrder} />}
          </span>
        </div>
        {showDateCreated && (
          <DateColumnHeader
            labelClassName={colLabel}
            sortKey={sortKey}
            sortOrder={sortOrder}
            onSort={onSort}
          />
        )}
        {showUser && (
          <div className="flex items-center justify-center whitespace-nowrap">
            {onSort ? (
              <button
                type="button"
                className={cn(
                  colLabel,
                  "inline-flex items-center rounded-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                )}
                onClick={() => onSort("user")}
              >
                {t.documents.table.uploader}
                <SortIcon field="user" activeField={sortKey} direction={sortOrder} />
              </button>
            ) : (
              <span className={colLabel}>{t.documents.table.uploader}</span>
            )}
          </div>
        )}
        {showVersion && (
          <div className={cn(colLabel, "text-center")}>{t.documents.table.version}</div>
        )}
        {showStatus && (
          <div className={cn(colLabel, "text-center")}>{t.documents.table.status}</div>
        )}
        {showTags && <div className={colLabel}>{t.documents.table.tags}</div>}
        {showParent && <div className={colLabel}>{t.documents.table.parent}</div>}
        {showActions && (
          <div className={cn(colLabel, "opacity-0")} aria-hidden="true">
            {t.documents.table.actions}
          </div>
        )}
      </div>

      {endContent && (
        <div className="mt-1 flex justify-end xl:absolute xl:right-6 xl:top-1/2 xl:z-10 xl:mt-0 xl:-translate-y-1/2 xl:pl-4">
          {endContent}
        </div>
      )}
    </div>
  );
}

/**
 * The one date column's header: its label sorts by whichever date is showing,
 * like every other column's label, and the caret beside it opens the
 * Uploaded / File modified switch.
 */
function DateColumnHeader({
  labelClassName,
  sortKey,
  sortOrder,
  onSort,
}: {
  labelClassName: string;
  sortKey?: SortKey;
  sortOrder?: SortOrder;
  onSort?: (key: SortKey) => void;
}) {
  const { t } = useLanguage();
  const basis = useDocumentListDateBasis();
  const sortField = DATE_BASIS_SORT_KEY[basis];
  const label =
    basis === "upload" ? t.documents.table.dateUploaded : t.documents.table.dateModifiedShort;

  // A list with no sort of its own gets no switch either: it is nested in a
  // page (the container dialog, behind a filters reset) or pinned (the share
  // page), and flipping a page-wide preference from there would reorder the
  // list behind it.
  const fixed = useIsDocumentListDateBasisFixed() || !onSort;

  // The list's date sort follows the choice where the sort is owned
  // (DocumentFiltersContext derives it), so this only records the choice.
  const switchTo = (next: DocumentListDateBasis) => {
    if (next !== basis) setDocumentListDateBasis(next);
  };

  const sortLabel = onSort ? (
    <button
      type="button"
      className={cn(labelClassName, "inline-flex cursor-pointer select-none items-center")}
      onClick={() => onSort(sortField)}
      aria-label={`${t.documents.table.sortByDate}: ${label}`}
      data-testid="date-column-sort"
    >
      {label}
      <SortIcon field={sortField} activeField={sortKey} direction={sortOrder} />
    </button>
  ) : (
    <span className={labelClassName}>{label}</span>
  );

  if (fixed) {
    return <div className="flex items-center justify-center whitespace-nowrap">{sortLabel}</div>;
  }

  return (
    <div className="flex items-center justify-center gap-0.5 whitespace-nowrap">
      {sortLabel}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              labelClassName,
              "inline-flex h-5 w-5 items-center justify-center rounded-sm hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            )}
            aria-label={`${t.documents.table.dateColumnSwitchLabel}: ${
              basis === "upload"
                ? t.documents.table.dateColumnUploadedOption
                : t.documents.table.dateColumnModifiedOption
            }`}
            data-testid="date-column-switch"
          >
            <ChevronDown className="h-3 w-3" aria-hidden="true" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-64">
          <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
            {t.documents.table.dateColumnSwitchLabel}
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={basis}
            onValueChange={(value) => switchTo(value as DocumentListDateBasis)}
          >
            <DropdownMenuRadioItem value="upload" className="flex-col items-start gap-0.5">
              <span>{t.documents.table.dateColumnUploadedOption}</span>
              <span className="text-xs text-muted-foreground">
                {t.documents.table.dateColumnUploadedHint}
              </span>
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="modified" className="flex-col items-start gap-0.5">
              <span>{t.documents.table.dateColumnModifiedOption}</span>
              <span className="text-xs text-muted-foreground">
                {t.documents.table.dateColumnModifiedHint}
              </span>
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// Sort affordance: every sortable column shows a permanently-visible indicator.
// The active column shows the directional up/down arrow; inactive sortable columns
// show a muted neutral up/down icon so the sort handle is discoverable at all times
// (per reporter feedback — not hover-only, not active-column-only). Size matches the
// standardized active arrow (`h-3 w-3`).
function SortIcon({
  field,
  activeField,
  direction,
}: {
  field: SortKey;
  activeField?: SortKey;
  direction?: SortOrder;
}) {
  if (field !== activeField) {
    return <ArrowUpDown className="ml-1 inline h-3 w-3 text-muted-foreground/50" />;
  }
  return direction === "asc" ? (
    <ArrowUp className="ml-1 inline h-3 w-3" />
  ) : (
    <ArrowDown className="ml-1 inline h-3 w-3" />
  );
}
